import { useCallback, useState } from 'react';
export const createApiState = (defaultData) => ({
    response: defaultData,
    loading: false,
    isCompleted: false,
    errors: [],
    error: undefined,
});
export const useApiCallback = (promise, defaultState) => {
    const [state, setState] = useState(createApiState(defaultState));
    const callback = useCallback(async (...args) => {
        setState((state) => ({ ...state, loading: true }));
        try {
            // get the response
            const response = await promise(...args);
            // some APIs return errors in the response, but still return a 200 status code.
            // this is why we check for errors in the response and set the errors in the state.
            const errors = (response?.errors || []).map(resolveErrorType);
            // set the state with the response and errors.
            setState((state) => ({
                ...state,
                loading: false,
                isCompleted: true,
                response,
                errors,
                error: errors[0],
            }));
            return response;
        }
        catch (err) {
            // When the promise is rejected, the error is passed to the catch block.
            // Usually this means the request returned a 4xx or 5xx error status.
            // if the error is not an instance of Error, create a new Error
            const error = resolveErrorType(err);
            // set the state with the error
            setState((state) => ({
                ...state,
                loading: false,
                isCompleted: true,
                response: err,
                errors: [error],
                error,
            }));
            //
            return err;
        }
    }, [promise]);
    return [state, callback, setState];
};
export const makeApiHook = (promise, defaultState) => () => {
    return useApiCallback(promise, defaultState);
};
const resolveErrorType = (err) => {
    if (err instanceof Error) {
        return err;
    }
    switch (typeof err) {
        case 'string':
            return new Error(err);
        case 'object':
            if ('message' in err) {
                return new Error(String(err.message));
            }
            else {
                return new Error(JSON.stringify({ error: err }));
            }
        default:
            return new Error(JSON.stringify({ error: err }));
    }
};
